import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Plus, Trash2, BookOpen, Search } from "lucide-react";

export default function Knowledge() {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [answer, setAnswer] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const canManage = user.role === "admin" || user.role === "manager";

  const load = () => api.get("/kb/articles").then(({ data }) => setArticles(data));
  useEffect(() => { load(); }, []);

  const askAi = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setAiBusy(true);
    setAnswer(null);
    try {
      const { data } = await api.post("/kb/ai-search", { query });
      setAnswer(data);
    } catch (err) {
      setAnswer({ answer: `Error: ${err.response?.data?.detail || err.message}`, sources: [] });
    } finally {
      setAiBusy(false);
    }
  };

  const categories = [...new Set(articles.map((a) => a.category))];

  return (
    <div className="space-y-6" data-testid="knowledge-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#00e5ff]">HR Knowledge</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Knowledge Base</h1>
        </div>
        {canManage && (
          <button onClick={() => setOpen(true)} data-testid="new-article" className="px-4 py-2.5 bg-[color:var(--accent)] text-black rounded-lg font-bold flex items-center gap-2 hover:bg-[color:var(--accent-hover)]">
            <Plus size={16} /> New Article
          </button>
        )}
      </div>

      {/* AI Search */}
      <div className="card-surface p-6 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[color:var(--accent)] to-transparent" />
        <div className="flex items-center gap-2 text-[color:var(--accent)] mb-3">
          <Sparkles size={16} />
          <span className="text-xs uppercase tracking-widest font-bold">Ask AI</span>
        </div>
        <form onSubmit={askAi} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="ai-query"
              placeholder="e.g. How many vacation days do I get?"
              className="w-full pl-10 pr-4 py-3 bg-[#0b0d12] border border-[#2a3447] rounded-lg outline-none focus:border-[color:var(--accent)]"
            />
          </div>
          <button type="submit" disabled={aiBusy} data-testid="ai-ask" className="px-6 py-3 bg-[color:var(--accent)] text-black rounded-lg font-bold hover:bg-[color:var(--accent-hover)] disabled:opacity-60">
            {aiBusy ? "Thinking…" : "Ask"}
          </button>
        </form>
        {answer && (
          <div className="mt-5 p-4 bg-[#0b0d12] border border-[#ff000044] rounded-lg" data-testid="ai-answer">
            <div className="text-xs uppercase tracking-widest text-[color:var(--accent)] mb-2">Answer</div>
            <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{answer.answer}</p>
          </div>
        )}
      </div>

      {/* Articles list */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {articles.map((a) => (
          <button
            key={a.id}
            onClick={() => setSelected(a)}
            data-testid={`article-${a.id}`}
            className="card-surface p-5 text-left hover:border-[#ff000044] transition-colors group"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#00e5ff20] text-[#00e5ff] flex items-center justify-center shrink-0">
                <BookOpen size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">{a.category}</div>
                <div className="font-bold mt-1 group-hover:text-[color:var(--accent)] transition-colors">{a.title}</div>
                <div className="text-xs text-slate-400 mt-2 line-clamp-2">{a.body}</div>
              </div>
            </div>
          </button>
        ))}
        {articles.length === 0 && <div className="text-slate-500 text-sm col-span-full">No articles yet.</div>}
      </div>

      {selected && <ArticleModal article={selected} canManage={canManage} onClose={() => setSelected(null)} onDeleted={() => { setSelected(null); load(); }} />}
      {open && <CreateModal onClose={() => setOpen(false)} onCreated={() => { load(); setOpen(false); }} />}
    </div>
  );
}

function ArticleModal({ article, canManage, onClose, onDeleted }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="card-surface p-8 w-full max-w-2xl max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="article-modal">
        <div className="text-[10px] uppercase tracking-widest text-[#00e5ff]">{article.category}</div>
        <h2 className="text-2xl font-bold mt-1">{article.title}</h2>
        <div className="text-xs text-slate-500 mt-2">by {article.author_name}</div>
        <div className="mt-5 whitespace-pre-wrap text-slate-200 leading-relaxed text-sm">{article.body}</div>
        <div className="mt-6 flex justify-between">
          {canManage ? (
            <button
              onClick={async () => { await api.delete(`/kb/articles/${article.id}`); onDeleted(); }}
              data-testid="delete-article"
              className="text-[#ff3b5c] hover:opacity-80 text-sm flex items-center gap-1"
            >
              <Trash2 size={14} /> Delete
            </button>
          ) : <div />}
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-[color:var(--accent)] text-black font-bold text-sm">Close</button>
        </div>
      </div>
    </div>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [body, setBody] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/kb/articles", { title, category, body, tags: [] });
    onCreated();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <form onSubmit={submit} className="card-surface p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="article-form">
        <h2 className="text-xl font-bold mb-4">New Article</h2>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" required data-testid="art-title" className="w-full mb-3 bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)]" />
        <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Category (e.g. Benefits)" required data-testid="art-cat" className="w-full mb-3 bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)]" />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Content" required rows={7} data-testid="art-body" className="w-full mb-3 bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)]" />
        <div className="mt-2 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200">Cancel</button>
          <button type="submit" data-testid="art-submit" className="px-4 py-2 rounded-lg bg-[color:var(--accent)] text-black font-bold text-sm">Publish</button>
        </div>
      </form>
    </div>
  );
}
